import type { Database } from '@/integrations/supabase/types';

// PDF form field types and utilities
export interface PDFField {
  id: string;
  name: string;
  type: 'text' | 'textarea' | 'email' | 'phone' | 'date' | 'checkbox' | 'select' | 'radio' | 'signature';
  label?: string;
  value?: string | boolean | number;
  x: number;
  y: number;
  width: number;
  height: number;
  pageNumber: number;
  required?: boolean;
  options?: string[];
  validation?: FieldValidation;
}

export interface FieldValidation {
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  custom?: (value: unknown) => string | null;
}

// Organization and user related types
export interface OrganizationData {
  id: string;
  name: string;
  email: string;
  created_at: string;
  settings?: OrganizationSettings;
}

export interface OrganizationSettings {
  branding_settings: {
    logo_url?: string;
    company_name?: string;
    primary_color: string;
    email_template: string;
    company_address?: string;
  };
  notification_preferences: {
    include_signed_pdf: boolean;
    notification_types: string[];
    email_notifications: boolean;
    include_audit_trail: boolean;
    notification_frequency: string;
  };
  pdf_generation_settings: {
    watermark_text: string;
    compression_level: string;
    include_watermark: boolean;
    include_timestamps: boolean;
    include_audit_metadata: boolean;
  };
  signature_settings: {
    certificate_id?: string;
    signature_type: string;
    signing_reason: string;
    signing_location: string;
    auto_sign_enabled: boolean;
  };
  expiry_notification_preferences: {
    enabled: boolean;
    reminder_days: number[];
    custom_message?: string;
    throttle_hours: number;
    email_notifications: boolean;
    send_to_organization: boolean;
    urgent_threshold_days: number;
    notification_frequency: string;
  };
}

// Form and submission related types
export interface FormData {
  [key: string]: string | number | boolean | File | File[] | undefined;
}

export interface SubmissionData {
  id: string;
  user_id: string;
  form_id: string;
  answers: FormData;
  created_at: string;
  updated_at?: string;
}

export interface ApplicationData {
  id: string;
  user_id: string;
  form_data: FormData;
  status: string;
  created_at: string;
  updated_at?: string;
  submissions?: SubmissionData;
}

// Invitation and template types
export interface InvitationData {
  id: string;
  organization_id: string;
  recipient_email: string;
  subject: string;
  message?: string;
  status: 'pending' | 'viewed' | 'completed' | 'expired';
  created_at: string;
  form_template?: FormTemplate;
}

export interface FormTemplate {
  id: string;
  title: string;
  description?: string;
  form_fields: FormFieldSchema[];
  is_active: boolean;
}

export interface FormFieldSchema {
  id?: string;
  name: string;
  type: string;
  label: string;
  required: boolean;
  placeholder?: string;
  rows?: number;
  options?: string[];
}

// Event and audit types
export interface AuditEventRecord {
  id: string;
  event_type: string;
  event_data: Record<string, unknown>;
  entity_type: string;
  entity_id: string;
  user_id: string;
  organization_id?: string;
  created_at: string;
}

// PDF processing types
export interface PDFViewport {
  width: number;
  height: number;
  transform: number[];
}

export interface PDFAnnotation {
  id: string;
  fieldName: string;
  fieldType: string;
  rect: number[];
  fieldValue?: string;
  options?: Array<{ exportValue: string; displayValue: string }>;
  multiLine?: boolean;
  password?: boolean;
  readOnly?: boolean;
  required?: boolean;
}

// Filter and search types
export interface FilterState {
  status: string;
  dateRange: string;
  search: string;
  program?: string;
  [key: string]: string | undefined;
}

// Shared utility types
export interface ApiResponse<T = unknown> {
  data?: T;
  error?: Error;
  success: boolean;
}

export interface RequestResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

// Guest flow types
export interface GuestInvitationState {
  invitation: InvitationData | null;
  organization: OrganizationData | null;
  isLoading: boolean;
  error?: string;
}

// ============================================================================
// PHASE 1: Enterprise Type Infrastructure (ESLint Remediation)
// ============================================================================

/**
 * SECURITY: Strongly typed error handling (OWASP A04 - Insecure Design mitigation)
 * Replaces 'any' in catch blocks to enforce explicit error handling
 */
export type UnknownError = unknown;

export function getErrorMessage(error: UnknownError): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }
  return 'An unexpected error occurred';
}

/**
 * ANALYTICS: Strongly typed event properties
 * Extends existing types to handle legacy pseudo_user_id + new userId
 */
export interface AnalyticsEventPropsExtended {
  userId?: string;
  pseudo_user_id?: string;
  sessionId?: string;
  session_id?: string;
  orgId?: string;
  organizationId?: string;
  [key: string]: unknown; // Preserve extensibility for new properties
}

/**
 * Helper to safely cast JSON props to analytics event props
 */
export function toAnalyticsProps(props: Database['public']['Tables']['analytics_events']['Row']['props']): AnalyticsEventPropsExtended | null {
  if (!props || typeof props !== 'object' || Array.isArray(props)) {
    return null;
  }
  return props as AnalyticsEventPropsExtended;
}

/**
 * PDF: Field value types (prevents injection attacks)
 */
export type PdfFieldValue = string | number | boolean | null;

/**
 * SDK: Strongly typed OneForm event handlers
 */
export interface OneFormReadyData {
  version?: string;
  fields?: string[];
  initialized?: boolean;
  formId?: string;
}

export interface OneFormSubmitData {
  submissionId?: string;
  templateId?: string;
  status?: 'success' | 'error';
  success?: boolean;
}

export interface OneFormErrorData {
  message?: string;
  code?: string;
}

export type OneFormEventHandler<T = unknown> = (data: T) => void;